import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperience,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import { Checkbox, FormControlLabel, Tab, Tabs } from '@material-ui/core';
import { UserCheck, Users } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Loader } from '../../../../components/Loader';
import { TableSearch } from '../../../../components/Table';
import { useErrorLogger } from '../../../../hooks/useErrorLogger';
import { useTranslation, useUnsafeTranslation } from '../../../../i18next';
import {
  SubHeadLineItem,
  SubHeadlineContainer
} from '../../../../layout/SubHeadlineContainer';
import { useStringQueryParam } from '../../../../routes';
import {
  useExperienceInstancesByTemplateId,
  useExperienceInstancesCurrentlyRelevant
} from '../../../../services/experiences/instance';
import {
  usePeopleTabV2Enabled,
  useTimelineItemAllTimePseudoInstances,
  useTimelineItemPseudoInstances
} from '../../../../services/experiences/timelineItemStatus';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { useTheme } from '../../../../themes';
import { ParticipantsTable } from '../../components/ParticipantsTable';
import { ParticipantDialog, ParticipantDialogLazy } from './ParticipantDialog';

const TABLE_HEIGHT = 500;

const TabsContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2),
  marginBottom: p.theme.spacing(4)
}));

const EmptyTable = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: p.theme.spacing(2),
  minHeight: p.theme.spacing(25),
  backgroundColor: p.theme.custom.colors.gray.w100,
  color: p.theme.custom.colors.gray.w500
}));

const TableActions = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

const ParticipantsInner = ({
  tpl,
  customFields, // unused atm - meant to be used to show more/other columns in the table. something we should do at some point
  instances,
  showOnlyActiveParticipants,
  setShowOnlyActiveParticipants,
  isCurrent
}: {
  tpl: Doc<IExperienceTemplate>;
  customFields: CustomUserField[];
  instances: Doc<IExperience>[];
  showOnlyActiveParticipants: boolean;
  setShowOnlyActiveParticipants: (nextState: boolean) => void;
  isCurrent: boolean;
}) => {
  const { t } = useTranslation();
  const { t: tUnsafe } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const theme = useTheme();

  const [q, setQ] = useStringQueryParam('q', '');

  const [dialogInstanceId, setDialogInstanceId] = useStringQueryParam('i', '');
  const setDialogInstance = useCallback(
    (inst: Doc<IExperience>) => setDialogInstanceId(inst.id),
    [setDialogInstanceId]
  );
  const dialogInstance =
    dialogInstanceId && instances
      ? instances.find((inst) => inst.id === dialogInstanceId) || null
      : null;
  // Under PEOPLE_TAB_V2 the list holds compact pseudo-instances (no full task/
  // automation/content data, no timing.end), so the detail dialog must re-fetch
  // the real instance by id rather than render the pseudo (which crashes the
  // timeline). V1 keeps passing the already-loaded full instance directly.
  const peopleTabV2Enabled = usePeopleTabV2Enabled();

  const tabs = [
    {
      key: PARTICIPANT_ACTOR_KEY,
      label: t('Participant')
    },
    ...Object.values(tpl.data.supportingActors).map((a) => ({
      key: a.key,
      label: a.name
    }))
  ];
  const [actor, setActor] = useState(tabs[0]);

  const trackInstanceViewLoaded = (instanceId: string) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_INSTANCE,
      name: 'Admin Instance View Loaded',
      'Instance ID': instanceId,
      Trigger: 'Participants Table',
      Explanation: 'Admin user views the details of a single instance'
    });
  };

  return (
    <>
      <SubHeadlineContainer collapse>
        {instances && (
          <SubHeadLineItem>
            <Users size={16} color={theme.custom.colors.gray.w500} />
            {isCurrent
              ? t('currentParticipantCount', { count: instances.length })
              : t('totalParticipantCount', {
                  count: instances.length
                })}
          </SubHeadLineItem>
        )}
        {tpl.data.enrollNewUsers && (
          <SubHeadLineItem>
            <UserCheck size={16} color={theme.custom.colors.gray.w500} />{' '}
            {t('autoEnrollHint')}
          </SubHeadLineItem>
        )}
      </SubHeadlineContainer>

      {tabs.length > 1 && (
        <TabsContainer>
          <Tabs
            value={tabs.findIndex((t) => t.key === actor.key) || 0}
            onChange={(ev, i) => {
              const nextActor = tabs[i] || tabs[0];
              setActor(nextActor);
            }}
            indicatorColor="primary"
            textColor="primary"
          >
            {tabs.map((t, i) => (
              <Tab key={t.key} value={i} label={t.label} />
            ))}
          </Tabs>
        </TabsContainer>
      )}
      <div>
        <TableActions>
          <FormControlLabel
            label={tUnsafe('Show only active experiences')}
            control={
              <Checkbox
                checked={showOnlyActiveParticipants}
                color="primary"
                onChange={(ev) =>
                  setShowOnlyActiveParticipants(ev.target.checked)
                }
              />
            }
          />
          <TableSearch
            term={q}
            onChange={setQ}
            placeholder={t('users.overview.search')}
          />
        </TableActions>
        {!instances.length && (
          <EmptyTable>{tUnsafe('No participants to display')}</EmptyTable>
        )}
        {!!instances.length && (
          <ParticipantsTable
            tpl={tpl}
            ds={instances}
            height={TABLE_HEIGHT}
            onRowClick={(d) => {
              trackInstanceViewLoaded(d.id);
              setDialogInstance(d);
            }}
            q={q}
            actor={actor}
          />
        )}
      </div>
      {dialogInstanceId &&
        (peopleTabV2Enabled ? (
          <ParticipantDialogLazy
            instanceId={dialogInstanceId}
            tplId={tpl.id}
            open={true}
            onClose={() => setDialogInstanceId('')}
          />
        ) : (
          dialogInstance && (
            <ParticipantDialog
              open={true}
              tpl={tpl}
              d={dialogInstance}
              onClose={() => setDialogInstanceId('')}
            />
          )
        ))}
    </>
  );
};

type ParticipantsViewProps = {
  tpl: Doc<IExperienceTemplate>;
  customFields: CustomUserField[];
  showOnlyActiveParticipants: boolean;
  setShowOnlyActiveParticipants: (nextState: boolean) => void;
};

const ParticipantsLoaded = ({
  instances,
  loading,
  error,
  isCurrent,
  ...props
}: ParticipantsViewProps & {
  instances: Doc<IExperience>[] | void;
  loading: boolean;
  error: unknown;
  isCurrent: boolean;
}) => {
  useErrorLogger('instance by tplId in range', error);
  if (loading) {
    return <Loader height={TABLE_HEIGHT} />;
  }
  if (error || !instances) {
    return null;
  }
  return <ParticipantsInner {...props} instances={instances} isCurrent={isCurrent} />;
};

const ParticipantsCurrentlyRelevantV1 = (props: ParticipantsViewProps) => {
  const [instances, loading, error] = useExperienceInstancesCurrentlyRelevant(
    props.tpl.data.orgId,
    props.tpl.id,
    props.tpl.data.expectedEndThreshold
  );
  return <ParticipantsLoaded {...props} instances={instances} loading={loading} error={error} isCurrent={false} />;
};

const ParticipantsCurrentlyRelevantV2 = (props: ParticipantsViewProps) => {
  const [instances, loading, error] = useTimelineItemPseudoInstances(
    props.tpl.data.orgId,
    props.tpl.id,
    props.tpl.data.expectedEndThreshold
  );
  return <ParticipantsLoaded {...props} instances={instances} loading={loading} error={error} isCurrent={false} />;
};

export const ParticipantsCurrentlyRelevant = (props: ParticipantsViewProps) =>
  usePeopleTabV2Enabled() ? (
    <ParticipantsCurrentlyRelevantV2 {...props} />
  ) : (
    <ParticipantsCurrentlyRelevantV1 {...props} />
  );

const ParticipantsAllTimeV1 = (props: ParticipantsViewProps) => {
  const [instances, loading, error] = useExperienceInstancesByTemplateId(
    props.tpl.data.orgId,
    props.tpl.id
  );
  return <ParticipantsLoaded {...props} instances={instances} loading={loading} error={error} isCurrent={true} />;
};

const ParticipantsAllTimeV2 = (props: ParticipantsViewProps) => {
  const [instances, loading, error] = useTimelineItemAllTimePseudoInstances(
    props.tpl.data.orgId,
    props.tpl.id
  );
  return <ParticipantsLoaded {...props} instances={instances} loading={loading} error={error} isCurrent={true} />;
};

export const ParticipantsAllTime = (props: ParticipantsViewProps) =>
  usePeopleTabV2Enabled() ? (
    <ParticipantsAllTimeV2 {...props} />
  ) : (
    <ParticipantsAllTimeV1 {...props} />
  );

export const Participants = ({
  tpl,
  customFields
}: {
  tpl: Doc<IExperienceTemplate>;
  customFields: CustomUserField[];
}) => {
  const [showOnlyActiveParticipants, setShowOnlyActiveParticipants] = useState(
    true
  );

  return showOnlyActiveParticipants ? (
    <ParticipantsCurrentlyRelevant
      tpl={tpl}
      customFields={customFields}
      showOnlyActiveParticipants={showOnlyActiveParticipants}
      setShowOnlyActiveParticipants={setShowOnlyActiveParticipants}
    />
  ) : (
    <ParticipantsAllTime
      tpl={tpl}
      customFields={customFields}
      showOnlyActiveParticipants={showOnlyActiveParticipants}
      setShowOnlyActiveParticipants={setShowOnlyActiveParticipants}
    />
  );
};
